




































































































import { Component, Watch, Prop } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import BaseTab from './baseTab'
import dayjs from 'dayjs'
import { ExStore } from 'vuex'
import { Tabs, TabSettingName } from '@/constants/appSettings'
import { PrescriptionMedicine } from '@/types/prescription'
import ky from 'ky'
import { isCordovaEnvironment } from '@/utils/cordova'

const TODAY = dayjs()

type Week = '1w' | '2w' | '3w' | '4w'

type MonthlyPrescription = Record<Week, number>

type PrescriptionSummary = Record<
  string,
  {
    type: PrescriptionMedicine['classification']
    name: PrescriptionMedicine['medicineName']
    code: PrescriptionMedicine['medicineCode']
    months: MonthlyPrescription[]
  }
>

const dateToMonth = (date: string): number => {
  return Number(date.substring(5, 7))
}

const dateToWeek = (date: string): Week => {
  const day = Number(date.slice(-2))
  if (day < 8) return '1w'
  if (day < 15) return '2w'
  if (day < 22) return '3w'
  return '4w'
}

@Component({
  components: {
    qrCode: () => import('@/components/common/qr/qr.vue'),
    qrDialog: () => import('@/components/common/qr/qrDialog.vue')
  }
})
export default class PrescriptionTab extends mixins(BaseTab) {
  @Prop({ default: false }) onLoadShowQr!: boolean
  $store!: ExStore
  prescriptions = {} as PrescriptionSummary
  showQR = false
  qrErrorMessage = ''
  medicationNote = ''

  get patient() {
    const patient = this.$store.getters['patient/currentPatient']
    this.medicationNote = patient?.medicationNote || ''
    return patient
  }

  get inCallWebView() {
    return this.$store.getters['videoCallRoom/inCallWebView']
  }

  get patientId() {
    return Number(this.$route.params.patientId)
  }

  get year() {
    if (Array.isArray(this.$route.query.year)) {
      return Number(this.$route.query.year[0]) || TODAY.year()
    }
    return Number(this.$route.query.year) || TODAY.year()
  }

  set year(year) {
    this.$router.push({
      name: Tabs[TabSettingName.prescription].routeName,
      query: { year: String(year) }
    })
  }

  @Watch('year')
  yearChange() {
    this.fetch()
  }

  get yearOptions() {
    const start = 2018
    const end = TODAY.year()
    return [...new Array(end - start + 1)].map((_, i) => end - i)
  }

  async fetch() {
    this.prescriptions = {}
    const { patientId, year } = this
    const res = await this.$api.prescription.fetch({ patientId, year })
    res.forEach(prescription => {
      const month = dateToMonth(prescription.prescriptionDate)
      const week = dateToWeek(prescription.prescriptionDate)
      prescription.medicines.forEach(medicine => {
        medicine.medicineCode
        if (!this.prescriptions[medicine.medicineCode]) {
          this.$set(this.prescriptions, medicine.medicineCode, {
            type: medicine.classification,
            name: medicine.medicineName || '【薬品名を表示できません】',
            code: medicine.medicineCode,
            months: [...new Array(12)].map(() => ({
              '1w': 0,
              '2w': 0,
              '3w': 0,
              '4w': 0
            }))
          })
        }
        this.$set(
          this.prescriptions[medicine.medicineCode].months[month - 1],
          week,
          this.prescriptions[medicine.medicineCode].months[month - 1][week] +
            medicine.dose * medicine.daysTimes
        )
      })
    })
  }

  get rangeMonth() {
    return [...new Array(12)].map((v, i) => i + 1)
  }

  created() {
    this.fetch()
    this.$postMessage.addEventListener('activateApp', () => {
      this.fetch()
    })
  }

  async mounted() {
    if (!this.onLoadShowQr) return
    await this.checkIsValidLogin()
    this.$store.dispatch('videoCallRoom/setInCallWebView')
    this.showQR = true
  }

  async checkIsValidLogin() {
    try {
      await this.$api.user.fetch()
    } catch (e) {
      this.$router.replace({ name: 'PatientLogin' })
    }
  }

  handleClickUploadButton() {
    this.qrErrorMessage = ''
    this.launchQrReader()
  }

  async QRData($event: string) {
    try {
      await this.$api.prescription.uploadQR({
        patientId: this.patientId,
        data: $event
      })
      await this.fetch()
      this.quitQrReader()
    } catch (e) {
      if (e instanceof ky.HTTPError) {
        if (e.response.status !== 400) {
          this.qrErrorMessage = '不正なQRコードです'
          return
        }
        const errors = await e.response.json()
        const errorList = Object.keys(errors).reduce(
          (list, key) => list.concat(errors[key]),
          [] as string[]
        )
        this.qrErrorMessage = errorList.join(', ')
        return
      } else {
        this.qrErrorMessage = '通信エラー'
      }
      console.error(e)
    }
  }

  launchQrReader() {
    if (!isCordovaEnvironment()) {
      this.showQR = true
      return
    }
    this.$postMessage.showPrescriptionQR(this.patientId)
  }

  quitQrReader() {
    if (!this.inCallWebView) {
      this.showQR = false
      return
    }
    this.$router.replace({ name: 'Hangup' })
  }

  async handleSaveComment() {
    try {
      await this.$api.user.patchPatient({
        patientId: this.patientId,
        params: { medicationNote: this.medicationNote }
      })
      await this.$store.dispatch('patient/updateCurrentPatient', {
        patientId: this.patientId
      })
    } catch (e) {
      //TODO: handle error
      console.error(e)
    }
  }
}
